import { FILTER_CATEGORIES, TAXONOMY_TYPES } from "@chef/constants";
import { slugify } from "@chef/helpers";
import { isEqualStrings, isNotEqualStrings } from "@chef/utils/equal";
import { isEmptyMap } from "@chef/utils/map";

export const allowedFilterCategoryTaxonomies = [
  TAXONOMY_TYPES.CATEGORY,
  TAXONOMY_TYPES.CAMPAIGN,
  TAXONOMY_TYPES.SPECIALFOOD,
] as string[];

type RecipeLike = {
  recipeId: number;
  mainRecipeId: number | null;
  recipeName: string;

  taxonomies: {
    name: string;
    type: string;
    description?: string;
  }[];
};

type RecipeLikeWithPrice = RecipeLike & {
  price?: number;
  productId?: never;
};

type RecipeLikeWithPriceAndProductId = RecipeLike & {
  price?: number;
  productId: string;
};

interface IFilterCategory {
  value: string;
  label: string;
  count: number;
  type: "default" | "highlighted" | "secondary";
  isAll: boolean;
  description?: string;
}

class FilterCategories {
  public categories: Map<string, FilterCategory>;

  private recommendations: Map<string, number>;
  private favorites: Set<number>;

  private implicits: Record<
    "all" | "favorites" | "recommendations",
    FilterCategory
  >;

  constructor(
    recommendations: Map<string, number> | null,
    favorites: { recipeId: number; mainRecipeId: number | null }[] | null,
  ) {
    this.implicits = {
      all: new FilterCategory(
        FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
        FILTER_CATEGORIES.ALL_CATEGORIES,
        "default",
      ),
      favorites: new FilterCategory(
        FILTER_CATEGORIES.FAVORITES_SLUG,
        FILTER_CATEGORIES.FAVORITES,
        "secondary",
      ),
      recommendations: new FilterCategory(
        FILTER_CATEGORIES.RECOMMENDATIONS_SLUG,
        FILTER_CATEGORIES.RECOMMENDATIONS,
        "secondary",
      ),
    };

    this.categories = new Map([
      [FILTER_CATEGORIES.ALL_CATEGORIES_SLUG, this.implicits.all],
      [FILTER_CATEGORIES.RECOMMENDATIONS_SLUG, this.implicits.recommendations],
      [FILTER_CATEGORIES.FAVORITES_SLUG, this.implicits.favorites],
    ]);

    this.recommendations = new Map([...(recommendations || [])]);

    this.favorites = new Set(
      favorites?.map((f) => f.mainRecipeId ?? f.recipeId) || [],
    );
  }

  has(slug: string) {
    return this.categories.has(slug);
  }

  get(slug: string): FilterCategory | undefined {
    return this.categories.get(slug);
  }

  create(
    slug: string,
    label: string,
    type: "default" | "highlighted" | "secondary",
    description?: string,
  ) {
    this.categories.set(
      slug,
      new FilterCategory(slug, label, type, description),
    );
  }

  insertRecipe(recipe: RecipeLikeWithPrice | RecipeLikeWithPriceAndProductId) {
    const taxonomies = recipe.taxonomies.filter((t) =>
      allowedFilterCategoryTaxonomies.includes(t.type),
    );
    for (const tax of taxonomies) {
      const slugifiedName = slugify(tax.name);

      if (!this.has(slugifiedName)) {
        this.create(
          slugifiedName,
          tax.name,
          tax.type === TAXONOMY_TYPES.CAMPAIGN ? "highlighted" : "default",
          tax.description,
        );
      }

      const c = this.get(slugifiedName);

      if (c) {
        c.addRecipe(recipe);

        this.implicits.all.addRecipe(recipe);

        if (
          this.favorites.has(recipe.recipeId) ||
          this.favorites.has(recipe.mainRecipeId ?? 0)
        ) {
          this.implicits.favorites.addRecipe(recipe);
        }

        if (this.recommendations.has(recipe.productId || "")) {
          this.implicits.recommendations.addRecipe(recipe);
        }
      }
    }
  }

  toArray(
    args: {
      exclude?: {
        all?: boolean;
        favorites?: boolean;
        recommendations?: boolean;
      };
    } = {},
  ): IFilterCategory[] {
    let values = [...this.categories.values()];

    const { exclude = {} } = args;

    if (exclude.all) {
      values = values.filter(
        (v) => v.slug !== FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
      );
    }

    if (exclude.favorites) {
      values = values.filter(
        (v) => v.slug !== FILTER_CATEGORIES.FAVORITES_SLUG,
      );
    }

    if (exclude.recommendations) {
      values = values.filter(
        (v) => v.slug !== FILTER_CATEGORIES.RECOMMENDATIONS_SLUG,
      );
    }

    return values.map((v) => v.output);
  }
}

class FilterCategory {
  public slug: string;
  public label: string;
  public type: "default" | "highlighted" | "secondary";
  public description?: string;
  public isAll: boolean;

  private recipes: Set<number>;

  constructor(
    slug: string,
    label: string,
    type: "default" | "highlighted" | "secondary",
    description?: string,
  ) {
    this.recipes = new Set();

    this.slug = slug;
    this.label = label;
    this.type = type;
    this.description = description;

    this.isAll = slug === FILTER_CATEGORIES.ALL_CATEGORIES_SLUG;
  }

  get count() {
    return this.recipes.size;
  }

  get output(): IFilterCategory {
    return {
      value: this.slug,
      label: this.label,
      count: this.count,
      type: this.type,
      isAll: this.isAll,
      description: this.description,
    };
  }

  addRecipe(recipe: RecipeLikeWithPriceAndProductId | RecipeLikeWithPrice) {
    this.recipes.add(recipe.recipeId);
  }
}

const generateTerms = (recipe: RecipeLike) => {
  return recipe.taxonomies
    .filter((t) => allowedFilterCategoryTaxonomies.includes(t.type))
    .map((t) => slugify(t.name))
    .join(" ");
};

const getFilterCategoriesFromRecipes = (
  recipes: RecipeLike[],
  recommendations: Map<string, number> | null,
  favorites: { recipeId: number; mainRecipeId: number | null }[] | null,
  excludeAll?: boolean,
) => {
  const categories = new FilterCategories(recommendations, favorites);

  for (const recipe of recipes) {
    categories.insertRecipe(recipe);
  }

  return categories.toArray({
    exclude: {
      all: excludeAll,
      favorites: !favorites,
      recommendations:
        !recommendations || (recommendations && isEmptyMap(recommendations)),
    },
  });
};

export const generateFilterCategoriesFromRecipes = (args: {
  recipes: RecipeLike[];
  currentFilter: string;
  recommendations: Map<string, number> | null;
  favorites: { recipeId: number; mainRecipeId: number | null }[] | null;
  excludeAll?: true;
  myPicks?: { amount: number };
  prioritiseRecommendations?: boolean;
}) => {
  const {
    recipes,
    excludeAll,
    recommendations,
    favorites,
    prioritiseRecommendations = false,
  } = args;

  const filterCategories = getFilterCategoriesFromRecipes(
    recipes,
    recommendations,
    favorites,
    excludeAll,
  );

  // sort in order of "All", highlighted, secondary, default
  filterCategories.sort((a, b) => {
    if (
      isEqualStrings(a.value, FILTER_CATEGORIES.ALL_CATEGORIES_SLUG) &&
      isNotEqualStrings(b.value, FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)
    ) {
      return -1;
    }

    if (
      isEqualStrings(b.value, FILTER_CATEGORIES.ALL_CATEGORIES_SLUG) &&
      isNotEqualStrings(a.value, FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)
    ) {
      return 1;
    }

    if (prioritiseRecommendations) {
      if (
        isEqualStrings(a.value, FILTER_CATEGORIES.RECOMMENDATIONS_SLUG) &&
        isNotEqualStrings(b.value, FILTER_CATEGORIES.RECOMMENDATIONS_SLUG)
      ) {
        return -1;
      }

      if (
        isEqualStrings(b.value, FILTER_CATEGORIES.RECOMMENDATIONS_SLUG) &&
        isNotEqualStrings(a.value, FILTER_CATEGORIES.RECOMMENDATIONS_SLUG)
      ) {
        return 1;
      }
    }

    if (
      isEqualStrings(a.type, "highlighted") &&
      isNotEqualStrings(b.type, "highlighted")
    ) {
      return -1;
    }

    if (
      isEqualStrings(b.type, "highlighted") &&
      isNotEqualStrings(a.type, "highlighted")
    ) {
      return 1;
    }

    if (
      isEqualStrings(a.type, "secondary") &&
      isNotEqualStrings(b.type, "secondary")
    ) {
      return -1;
    }

    if (
      isEqualStrings(b.type, "secondary") &&
      isNotEqualStrings(a.type, "secondary")
    ) {
      return 1;
    }

    return a.label.localeCompare(b.label);
  });

  if (prioritiseRecommendations) {
    const recommendationsCategory = filterCategories.find((c) =>
      isEqualStrings(c.value, FILTER_CATEGORIES.RECOMMENDATIONS_SLUG),
    );

    if (recommendationsCategory) {
      recommendationsCategory.type = "highlighted";
    }
  }

  return filterCategories;
};

interface IShouldShowRecipeByFilterBase {
  recipe: RecipeLike;
  // | PickAndMixQuery["pickAndMix"][number]["recipes"][number]
  // | RecipesByProductIdListQuery["recipesByProductIdList"][number]["products"][number]["recipes"][number]
  // | Order["orderLines"][number]["recipes"][number];
  filter: string;
  searchValue?: string;

  recommendations?: never;
  favorites: { recipeId: number; mainRecipeId: number | null }[] | null;
  productId?: never;

  price: number;
}

interface IShouldShowRecipeByFilterWithProductId {
  recipe: RecipeLike;
  filter: string;
  searchValue?: string;

  recommendations: Map<string, number> | null;
  favorites: { recipeId: number; mainRecipeId: number | null }[] | null;
  productId: string;

  price: number;
}

export const shouldShowRecipeByFilter = (
  args: IShouldShowRecipeByFilterBase | IShouldShowRecipeByFilterWithProductId,
) => {
  const { recipe, filter, searchValue, recommendations, productId, favorites } =
    args;

  const terms = generateTerms(recipe);

  if (searchValue) {
    // Show recipe if the id, name or taxonomies match
    const search = searchValue.toLowerCase();

    return (
      recipe.recipeId.toString().includes(search) ||
      recipe.recipeName.toLowerCase().includes(search) ||
      terms.includes(search)
    );
  }

  if (filter === FILTER_CATEGORIES.ALL_CATEGORIES_SLUG) {
    return true;
  }

  if (filter === FILTER_CATEGORIES.RECOMMENDATIONS_SLUG) {
    return recommendations?.has(productId.toLowerCase());
  }

  if (filter === FILTER_CATEGORIES.FAVORITES_SLUG) {
    return favorites?.some(
      (f) =>
        f.mainRecipeId === recipe.mainRecipeId ||
        f.recipeId === recipe.recipeId,
    );
  }

  // Check if the filter is in the terms
  if (terms.includes(filter)) {
    return true;
  }

  return false;
};
